var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { property, query } from 'lit/decorators.js';
import { html, css } from 'lit';
import { numberDecoder, booleanDecoder, chunkDecoder, customElement, PICInput } from '../Internals';
let PICTextArea = class PICTextArea extends PICInput {
    get submitValue() {
        return this.inputField.value;
    }
    constructor() {
        super();
        this.rows = 0;
        this.locksize = false;
        this.append_ = false;
        this.spellcheck = false;
        this.monospace = false;
        this.showcharactercount = false;
        this.resizing = false;
    }
    static get styles() {
        const defaultStyle = css `
        :host {
            position: relative;
        }
        .inner-input {
            height: auto; 
            padding-top: 2px;
            border: 1px solid var(--pic-innerInputBorderColor, black);
        }
        .inner-input.locksize{
            resize: none;
        }
        .inner-input.monospace {
            font-family: monospace;
        }
        .inner-input:disabled {
            border-color: var(--pic-innerInputBorderDisabledColor);
            background-color: var(--pic-surfaceColor);
            color: var(--pic-disabledColor);
        }
        .inner-input:focus{
            border: 2px solid var(--pic-innerInputBorderFocusColor, black);
        }
        .countdownContainer {
            position: absolute;
            bottom: -10px;
            right: 0;
            width: fit-content;
            display: flex;
            justify-content: end;
            height: 10px;
        }
        .countdownContainer :first-child {
            margin: 0;
            font-size: 8px;
            line-height: 10px;
        }
    `;
        return css `
            ${super.styles}
            ${defaultStyle}
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        let tmpVal = chunkDecoder(this);
        if (tmpVal > '') {
            this.value = tmpVal;
            this.requestUpdate();
        }
    }
    focus() {
        this.inputField.focus();
    }
    firstUpdated() {
        var _a;
        // if(this.eType !== 'old') this.decodeInitTextVal();
        this.initValue_ = this.submitValue;
        let textarea = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('textarea');
        this.textArea = textarea.clientHeight;
        // keep errorMessage moving with resizing textarea
        this.resizeObserver = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                var _a;
                let error = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('pic-error');
                if (error) {
                    if (this.density === 'inline') {
                        error.style.setProperty('--pic-errorTop', (entry.target.clientHeight / 2) + 9 + 'px');
                    }
                    else {
                        error.style.setProperty('--pic-errorTop', entry.target.clientHeight + 9 + 'px');
                    }
                }
            });
        });
        this.resizeObserver.observe(this);
        this.resizing = false;
        if (this.focus_)
            this.focus();
    }
    handleDrop(event) {
        event.preventDefault();
        let ref = this;
        let txt = ref.value;
        function handleText(iText) {
            if (ref.append_) {
                txt += iText;
            }
            else {
                txt = iText;
            }
        }
        if (event.dataTransfer) {
            // if browser supports DataTransferItemList
            if (event.dataTransfer.items) {
                if (event.dataTransfer.items.length === 1 && event.dataTransfer.items[0].kind === 'file') {
                    // Use DataTransferItemList interface to access the file(s)
                    for (var i = 0; i < event.dataTransfer.items.length; i++) {
                        // If dropped items aren't files, reject them
                        if (event.dataTransfer.items[i].kind === 'file') {
                            let file = event.dataTransfer.items[i].getAsFile();
                            console.log('... file[' + i + '].name = ' + file.name);
                            file.text().then((fileCont) => { ref.value = fileCont; ref.requestUpdate(); });
                        }
                    }
                }
                else {
                    //prevent
                }
            }
            else {
                if (event.dataTransfer.files.length === 1) {
                    // Use DataTransfer interface to access the file(s)
                    for (var i = 0; i < event.dataTransfer.files.length; i++) {
                        console.log('... file[' + i + '].name = ' + event.dataTransfer.files[i].name);
                        console.log(event.dataTransfer.files[i].text().then((fileCont) => { handleText(fileCont); }));
                    }
                }
                else {
                    //PREVENT
                }
            }
            this.value = txt;
            this.requestUpdate();
        }
    }
    renderCharacterCount() {
        var _a, _b;
        let textArea = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('textarea');
        let maxValue = (_b = this.validateData) === null || _b === void 0 ? void 0 : _b.rules[0].value;
        if (this.validateData === undefined) {
            return html `
            <div class="countdownContainer">
                <p class="countdownText pic-caption">${textArea === null || textArea === void 0 ? void 0 : textArea.value.length}</p>
            </div>
            `;
        }
        else {
            return html `
            <div class="countdownContainer">
                <p class="countdownText pic-caption">${textArea === null || textArea === void 0 ? void 0 : textArea.value.length}/${maxValue}</p>
            </div>
            `;
        }
    }
    getInnerInputClass() {
        let innerInputClass = super.getInnerInputClass();
        if (this.locksize)
            innerInputClass['locksize'] = true;
        if (this.monospace)
            innerInputClass['monospace'] = true; // Add monospace class conditionally
        innerInputClass['txtarea'] = true;
        return innerInputClass;
    }
    changeHandler(e) {
        this.validate();
        return super.changeHandler(e);
    }
    focusHandler(mode) {
        this.mode = mode;
        if (mode === 'in') {
            this.showFullError = true;
            this.requestUpdate();
        }
        if (mode === 'out') {
            this.showFullError = false;
            this.requestUpdate();
        }
    }
    getTextAreaStyle() {
        let styles = [];
        if (this.validState.length > 0) {
            styles.push({ 'border-color': '#b50000' });
            styles.push({ 'border-width': '2px' });
            styles.push({ 'padding': '1px 1px 0 6px' });
        }
        else {
            if (this.mode === 'in') {
                styles.push({ 'border': '2px solid var(--pic-innerInputBorderFocusColor, black)' });
                styles.push({ 'padding': '1px 1px 0 6px' });
            }
            else {
                if (!this.disabled_) {
                    styles.push({ 'padding': '2px 1px 0 7px' });
                }
            }
        }
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    getTextContainerStyle() {
        let styles = [];
        styles.push({ 'position': 'relative' });
        if (this.density == 'inline') {
            styles.push({ 'flex-flow': 'row' });
            styles.push({ 'align-items': 'center' });
            styles.push({ 'width': 'fit-content' });
        }
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    setComponentStyle() {
        if (this.showcharactercount) {
            this.style.setProperty('margin-bottom', '10px');
        }
    }
    render() {
        this.setComponentStyle();
        this.setComponentInlineStyle();
        return html `
            ${this.renderCSS(true)}
            <style> 
                .inner-input-default.low.txtarea {
                    height: calc((var(--pic-densityLowInputHeight)*${this.rows}) + 0.5rem);
                }
                .inner-input-default.medium.txtarea {
                    height: calc((var(--pic-densityMediumInputHeight)*${this.rows}) + 0.5rem);             
                }
                .inner-input-default.high.txtarea {
                    height: calc((var(--pic-densityHighInputHeight)*${this.rows}) + 0.5rem);          
                }
                .inner-input-default.inline.txtarea {
                    height: calc((var(--pic-densityInlineInputHeight)*${this.rows}) + 0.5rem);
                }
            </style>

            <div style=${styleMap(this.getTextContainerStyle())} class=${classMap(this.getContainerClassMap())}>
                <label class=${classMap(this.getInputLabelClass())} for=${this.id_ + '_inner'}>${this.label}</label>
                <textarea
                    style=${styleMap(this.getTextAreaStyle())}
                    id=${this.id_ + '_inner'}
                    class=${classMap(this.getInnerInputClass())}
                    rows=${this.rows}
                    ?disabled=${this.disabled_}
                    placeholder=${this.placeholder}
                    @drop=${this.handleDrop}
                    @input=${this.validate}
                    .value=${this.value}
                    ?readonly=${this.readonly_}
                    spellcheck=${this.spellcheck}
                    @focusin=${() => this.focusHandler('in')}
                    @focusout=${(e) => { this.changeHandler(e); this.focusHandler('out'); }}
                    >
                </textarea>
                ${this.showcharactercount || (this.validateData && this.showcharactercount) ? this.renderCharacterCount() : ''}
                ${this.tooltip_ ? html `<pic-tooltip>${this.tooltip_}</pic-tooltip>` : ''}
                ${this.renderErr(this.showFullError)}
            </div>
        `;
    }
};
__decorate([
    query('.inner-input')
], PICTextArea.prototype, "inputField", void 0);
__decorate([
    property({ converter: numberDecoder })
], PICTextArea.prototype, "rows", void 0);
__decorate([
    property({ type: Boolean })
], PICTextArea.prototype, "locksize", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'append', converter: booleanDecoder })
], PICTextArea.prototype, "append_", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'spellcheck', converter: booleanDecoder })
], PICTextArea.prototype, "spellcheck", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'monospace', converter: booleanDecoder })
], PICTextArea.prototype, "monospace", void 0);
__decorate([
    property({ type: Boolean, attribute: 'showcharactercount', converter: booleanDecoder })
], PICTextArea.prototype, "showcharactercount", void 0);
PICTextArea = __decorate([
    customElement('pic-textarea')
], PICTextArea);
export { PICTextArea };
//# sourceMappingURL=PICTextArea.js.map